import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const AuthContext = createContext({ user: null, loading: true });

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);   // null = not authed | object = authed
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      // Silent probe — 401 on the public landing is expected. Suppress console noise.
      const me = await api.authMe().catch((e) => {
        if (e?.response?.status === 401) return null;
        throw e;
      });
      setUser(me || null);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL — Si on est de retour d'un OAuth callback (`#session_id=` dans
    // l'URL), on saute `/auth/me` : c'est `AuthCallback` qui va d'abord
    // échanger le session_id avec le backend et poser le cookie.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const u = await api.authLogin(email, password);
    setUser(u);
    return u;
  };
  const register = async (payload) => {
    const u = await api.authRegister(payload);
    // Feb 2026 §« Onboarding démo » — Chaque nouveau compte démarre dans
    // l'espace démo pour se familiariser avec l'interface. L'utilisateur
    // peut le quitter à tout moment depuis Paramètres → « Quitter la
    // démo ». On tolère un échec silencieux (compte quand même créé).
    try { await api.loadDemo(); } catch { /* ignore */ }
    // Recharge le user pour récupérer le flag demo_mode marqué côté
    // serveur par /api/demo/load.
    try {
      const fresh = await api.authMe();
      setUser(fresh || u);
      return fresh || u;
    } catch {
      setUser(u);
      return u;
    }
  };
  const logout = async () => {
    // On tente les deux voies : JWT + Google. La 401 sur la voie non utilisée
    // est bénigne et silencieusement ignorée.
    try { await api.authLogout(); } catch {}
    try { await api.authGoogleLogout(); } catch {}
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() { return useContext(AuthContext); }
